/**
 * Activity Service
 * Handles API calls for retrieving audit logs/activity feed
 */

import { apiClient } from './api.client';
import { AuditLog, AuditAction, ResourceType } from '@creatoros/shared';

/**
 * Parameters for fetching activities with filters
 */
export interface GetActivitiesParams {
  /** Filter by single or multiple action types */
  action?: AuditAction | AuditAction[];
  /** Filter by resource type */
  resourceType?: ResourceType;
  /** Filter by start date (ISO 8601 format) */
  startDate?: string;
  /** Filter by end date (ISO 8601 format) */
  endDate?: string;
  /** Search text for filtering by deal/brand names (backend database search) */
  searchText?: string;
  /** Number of items per page (default: 20, max: 100) */
  limit?: number;
  /** Continuation token for pagination */
  continuationToken?: string;
}

/**
 * Response for fetching activities
 */
export interface GetActivitiesResponse {
  /** List of activity logs */
  activities: AuditLog[];
  /** Token for fetching the next page */
  continuationToken?: string;
  /** Whether more results are available */
  hasMore: boolean;
  /** Number of items in current page */
  count: number;
}

/**
 * Activity Service
 */
export const activityService = {
  /**
   * Fetch activitieswith optional filters and pagination
   * @param params - Filter and pagination parameters
   * @returns Activities and pagination info
   */
  async getActivities(params: GetActivitiesParams = {}): Promise<GetActivitiesResponse> {
    const queryParams = new URLSearchParams();

    // Handle action filter (single or multiple)
    if (params.action) {
      const actions = Array.isArray(params.action) ? params.action : [params.action];
      queryParams.set('action', actions.join(','));
    }

    if (params.resourceType) {
      queryParams.set('resourceType', params.resourceType);
    }

    if (params.startDate) {
      queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams.set('endDate', params.endDate);
    }

    if (params.searchText) {
      queryParams.set('search', params.searchText);
    }

    if (params.limit) {
      queryParams.set('limit', params.limit.toString());
    }

    if (params.continuationToken) {
      queryParams.set('continuationToken', params.continuationToken);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/api/activities?${queryString}` : '/api/activities';

    const response = await apiClient.get<{ success: boolean; data: GetActivitiesResponse }>(url);

    return response.data.data;
  },

  /**
   * Get single activity by ID (optional - for detail view)
   * Note: This requires a backend endpoint that doesn't exist yet
   * @param id - Activity ID
   * @returns Single audit log
   */
  async getActivityById(id: string): Promise<AuditLog> {
    const response = await apiClient.get<{ success: boolean; data: AuditLog }>(
      `/api/activities/${id}`
    );
    return response.data.data;
  },
};
